import { ConsultFieldTypeEnum, ServiceTypeEnum } from 'utils/enums';

const CTPreliminarilyOptions = [
  {
    value: 'PreliminarilyReviewedByMe',
    label: 'preliminarily reviewed by me, please refer to radiology',
    summaryLabel:
      'I visualized and interpreted this study as described. Official radiology read to follow',
  },
  { value: 'PerRadiologistRead', label: 'per radiologist read' },
];

const imagingReviewedOptions = [
  {
    value: 'ImagingReviewedWithAttendingNeurologist',
    label: 'imaging reviewed with attending neurologist',
  },
  {
    value: 'ImagingPersonallyReviewedByMe',
    label: 'imaging personally reviewed by me',
  },
];

const dataFields = {
  glucosePredefined: {
    name: 'glucosePredefined',
    title: 'Glucose',
    type: ConsultFieldTypeEnum.TOGGLE,
    options: [
      {
        value: 'WithinNormalLimits',
        label: 'WNL',
        summaryLabel: 'within normal limits',
      },
      { value: 'Hypoglycemia', label: 'hypoglycemia' },
      { value: 'Hyperglycemia', label: 'hyperglycemia' },
    ],
  },
  glucoseFreeText: {
    name: 'glucoseFreeText',
    associatedField: 'glucosePredefined',
    type: ConsultFieldTypeEnum.INPUT,
    multiline: true,
  },
  creatininePredefined: {
    name: 'creatininePredefined',
    title: 'Creatinine',
    type: ConsultFieldTypeEnum.TOGGLE,
    options: [
      {
        value: 'WithinNormalLimits',
        label: 'WNL',
        summaryLabel: 'within normal limits',
      },
      { value: 'Elevated', label: 'elevated' },
    ],
  },
  creatinineFreeText: {
    name: 'creatinineFreeText',
    associatedField: 'creatininePredefined',
    type: ConsultFieldTypeEnum.INPUT,
    multiline: true,
  },
  inrPredefined: {
    name: 'inrPredefined',
    title: 'INR',
    type: ConsultFieldTypeEnum.TOGGLE,
    options: [
      {
        value: 'WithinNormalLimits',
        label: 'WNL',
        summaryLabel: 'within normal limits',
      },
      { value: 'Therapeutinc', label: 'therapeutic' },
      { value: 'Elevated', label: 'elevated' },
      { value: 'Over', label: '> 1.7', summaryLabel: 'over 1.7' },
      { value: 'OverAnticoagulated', label: 'over anticoagulated' },
    ],
  },
  inrFreeText: {
    name: 'inrFreeText',
    associatedField: 'inrPredefined',
    type: ConsultFieldTypeEnum.INPUT,
    multiline: true,
  },
  otherLabs: {
    name: 'otherLabs',
    label: 'Other Labs',
    type: ConsultFieldTypeEnum.INPUT,
    multiline: true,
  },
  headCtPersonallyReadAt: {
    name: 'headCtPersonallyReadAt',
    label: 'Time Head CT personally read by me',
    type: ConsultFieldTypeEnum.DATETIME,
  },
  headCtPredefined: {
    name: 'headCtPredefined',
    label: 'Head CT',
    type: ConsultFieldTypeEnum.CHIP,
    options: [
      { value: 'NoBleed', label: 'no bleed' },
      { value: 'EarlyIschemicChange', label: 'early ischemic change' },
      { value: 'McaSign', label: 'MCA sign' },
      { value: 'Hemorrhage', label: 'hemorrhage' },
      {
        value: 'PersonallyReviewedByMe',
        label: 'personally reviewed by me',
        showOn: [ServiceTypeEnum.CLINIC],
      },
      {
        value: 'PreliminarilyReviewedByMeReferToRadiologyRead',
        label: 'preliminarily reviewed by me, refer to radiology read',
        summaryLabel:
          'I visualized and interpreted this study as described. Official radiology read to follow',
        showOn: [ServiceTypeEnum.NEURO, ServiceTypeEnum.TELE_NEURO],
      },
      { value: 'PerRadiologistRead', label: 'per radiologist read' },
      { value: 'PreliminaryRead', label: 'preliminary read' },
      { value: 'SubacuteIschemicStroke', label: 'subacute ischemic stroke' },
    ],
  },
  headCtFreeText: {
    name: 'headCtFreeText',
    associatedField: 'headCtPredefined',
    type: ConsultFieldTypeEnum.INPUT,
    multiline: true,
  },
  ctaPersonallyReviewedAt: {
    name: 'ctaPersonallyReviewedAt',
    label: 'Time CTA personally reviewed by me',
    type: ConsultFieldTypeEnum.DATETIME,
  },
  ctaHeadPredefined: {
    name: 'ctaHeadPredefined',
    title: 'CTA Head',
    type: ConsultFieldTypeEnum.TOGGLE,
    options: [
      {
        value: 'NoLargeVesselOcclusion',
        label: 'no LVO',
        summaryLabel: 'no large vessel occlusion',
      },
      { value: 'McaOcclusion', label: 'MCA occlusion' },
      { value: 'Dissection', label: 'dissection' },
      { value: 'Aneurysm', label: 'aneurysm' },
      {
        value: 'ArteriovenousMalformation',
        label: 'AVM',
        summaryLabel: 'arteriovenous malformation',
      },
    ],
  },
  ctaHeadPreliminarilyPredefined: {
    name: 'ctaHeadPreliminarilyPredefined',
    associatedField: 'ctaHeadPredefined',
    type: ConsultFieldTypeEnum.TOGGLE,
    options: CTPreliminarilyOptions,
  },
  ctaHeadFreeText: {
    name: 'ctaHeadFreeText',
    associatedField: 'ctaHeadPredefined',
    type: ConsultFieldTypeEnum.INPUT,
    multiline: true,
  },
  ctaNeckPredefined: {
    name: 'ctaNeckPredefined',
    title: 'CTA Neck',
    type: ConsultFieldTypeEnum.TOGGLE,
    options: [
      { value: 'PatentVessels', label: 'patent vessels' },
      { value: 'Dissection', label: 'dissection' },
      { value: 'Occlusion', label: 'occlusion' },
    ],
  },
  ctaNeckPreliminarilyPredefined: {
    name: 'ctaNeckPreliminarilyPredefined',
    associatedField: 'ctaNeckPredefined',
    type: ConsultFieldTypeEnum.TOGGLE,
    options: CTPreliminarilyOptions,
  },
  ctaNeckFreeText: {
    name: 'ctaNeckFreeText',
    associatedField: 'ctaNeckPredefined',
    type: ConsultFieldTypeEnum.INPUT,
    multiline: true,
  },
  ctpPersonallyReviewedAt: {
    name: 'ctpPersonallyReviewedAt',
    label: 'Time CTP reviewed by me',
    type: ConsultFieldTypeEnum.DATETIME,
  },
  ctPerfusionPredefined: {
    name: 'ctPerfusionPredefined',
    label: 'CT Perfusion',
    type: ConsultFieldTypeEnum.CHIP,
    options: [
      { value: 'WNL', label: 'WNL' },
      { value: 'Mismatch', label: 'mismatch' },
      { value: 'MatchedDefect', label: 'matched defect' },
      { value: 'CoreInfarctSize', label: 'core infarct size' },
    ],
  },
  ctPerfusionPreliminarilyPredefined: {
    name: 'ctPerfusionPreliminarilyPredefined',
    associatedField: 'ctPerfusionPredefined',
    type: ConsultFieldTypeEnum.TOGGLE,
    options: CTPreliminarilyOptions,
  },
  ctPerfusionFreeText: {
    name: 'ctPerfusionFreeText',
    associatedField: 'ctPerfusionPredefined',
    type: ConsultFieldTypeEnum.INPUT,
    multiline: true,
  },
  mriPersonallyReviewedAt: {
    name: 'mriPersonallyReviewedAt',
    label: 'Time MRI reviewed by me',
    type: ConsultFieldTypeEnum.DATETIME,
  },
  mriPredefined: {
    name: 'mriPredefined',
    title: 'MRI',
    type: ConsultFieldTypeEnum.TOGGLE,
    options: [
      { value: 'NoAcuteIschemia', label: 'no acute ischemia' },
      { value: 'AcuteIschemia', label: 'acute ischemia' },
      { value: 'SubacuteIschemia', label: 'subacute ischemia' },
      { value: 'Mass', label: 'mass' },
      { value: 'Hemorrhage', label: 'hemorrhage' },
    ],
  },
  mriPreliminarilyPredefined: {
    name: 'mriPreliminarilyPredefined',
    associatedField: 'mriPredefined',
    type: ConsultFieldTypeEnum.TOGGLE,
    options: CTPreliminarilyOptions,
  },
  mriFreeText: {
    name: 'mriFreeText',
    associatedField: 'mriPredefined',
    type: ConsultFieldTypeEnum.INPUT,
    multiline: true,
  },
  otherImaging: {
    name: 'otherImaging',
    label: 'Other Imaging',
    type: ConsultFieldTypeEnum.INPUT,
    multiline: true,
  },
  currentHospitalMedicationsPredefined: {
    name: 'currentHospitalMedicationsPredefined',
    label: 'Current Hospital Medications',
    type: ConsultFieldTypeEnum.CHIP,
    options: [
      { value: 'MedicationListReviewed', label: 'Medication list reviewed' },
      { value: 'Placeholder', label: '***' },
    ],
  },
  currentHospitalMedicationsFreeText: {
    name: 'currentHospitalMedicationsFreeText',
    type: ConsultFieldTypeEnum.INPUT,
    associatedField: 'currentHospitalMedicationsPredefined',
    multiline: true,
  },
  echocardiogram: {
    name: 'echocardiogram',
    label: 'Echocardiogram',
    type: ConsultFieldTypeEnum.CHIP,
    options: [
      { value: 'TEE', label: 'TEE' },
      { value: 'TTE', label: 'TTE' },
    ],
  },
  echocardiogramOther: {
    name: 'echocardiogramOther',
    type: ConsultFieldTypeEnum.INPUT,
    associatedField: 'echocardiogram',
    multiline: true,
  },
  eegOther: {
    name: 'eegOther',
    type: ConsultFieldTypeEnum.INPUT,
    label: 'EEG',
    rows: 2,
    multiline: true,
  },
};

export const teleNeuroDataFields = {
  ...dataFields,
  ctaHeadPredefined: {
    ...dataFields.ctaHeadPredefined,
    label: 'CTA Head / Neck',
    type: ConsultFieldTypeEnum.CHIP,
  },
  ctaNeckPredefined: {
    ...dataFields.ctaNeckPredefined,
    associatedField: 'ctaHeadPredefined',
    type: ConsultFieldTypeEnum.CHIP,
    title: null,
    options: [
      ...dataFields.ctaNeckPredefined.options,
      ...imagingReviewedOptions,
    ],
  },
  ctaNeckPreliminarilyPredefined: {
    ...dataFields.ctaNeckPreliminarilyPredefined,
    associatedField: 'ctaHeadPredefined',
    type: ConsultFieldTypeEnum.CHIP,
  },
  ctaHeadctaNeckFreeText: {
    name: 'ctaHeadctaNeckFreeText',
    type: ConsultFieldTypeEnum.INPUT,
    associatedField: 'ctaHeadPredefined',
    multiline: true,
  },
  mriPredefined: {
    ...dataFields.mriPredefined,
    label: 'MRI',
    type: ConsultFieldTypeEnum.CHIP,
    options: [...dataFields.mriPredefined.options, ...imagingReviewedOptions],
  },
  mriPreliminarilyPredefined: {
    ...dataFields.mriPreliminarilyPredefined,
    type: ConsultFieldTypeEnum.CHIP,
  },
  otherLabs: {
    ...dataFields.otherLabs,
    label: null,
    summaryLabel: 'Other Labs',
  },
  otherLabsPredefined: {
    name: 'otherLabsPredefined',
    type: ConsultFieldTypeEnum.CHIP,
    label: 'Other Labs',
    summaryLabel: null,
    options: [{ value: 'Placeholder', label: '***' }],
    associatedField: 'otherLabs',
  },
};

export default dataFields;
